import type { AxiosInstance } from 'axios';

import { invertMap } from '@/sections/insights/queryBuilder/entities/common/invert';
import {
  getNotFromSimpleFilter,
  getOperatorFromQueryFilter,
  getOperatorFromSimpleFilter,
  getValueFromQueryFilter,
  getValueFromSimpleFilter,
} from '@/sections/insights/queryBuilder/entities/common/translators';
import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';
import type { QueryFilter } from '@/types';
import type { VulnerabilityStatusMode } from '@/utils/vulnerabilityStatus.util';
import { isValidMode } from '@/utils/vulnerabilityStatus.util';

const operatorMap: Record<string, string> = {
  'STARTS WITH': 'is',
};

const operatorMapInverse: Record<string, string> = {
  is: 'STARTS WITH',
};

const LEGACY_VALUE_MAP: Record<string, string> = {
  T: 'Pending',
  O: 'Open',
  R: 'Closed',
  I: 'Accepted',
  D: 'Rejected',
};

const NEW_VALUE_MAP: Record<string, string> = {
  T: 'Detected',
  O: 'Demonstrated',
  R: 'Resolved',
  I: 'Accepted',
  D: 'Rejected',
};

// Module-level maps (populated by fetchStatusMode)
let valueMap: Record<string, string> = LEGACY_VALUE_MAP;
let valueMapInverse: Record<string, string> = invertMap(LEGACY_VALUE_MAP);

// Export for testing
export const __setStatusMapsForTesting = (mode: VulnerabilityStatusMode) => {
  valueMap = mode === 'new' ? NEW_VALUE_MAP : LEGACY_VALUE_MAP;
  valueMapInverse = invertMap(valueMap);
};

/**
 * Fetch the vulnerability-status-system setting to determine display labels.
 * Defaults to 'legacy' if the setting is missing or invalid.
 */
async function fetchStatusOptions(axios: AxiosInstance): Promise<string[]> {
  try {
    const response = await axios.get<{
      settings: Array<{ name: string; value: string }>;
    }>('/my', {
      params: {
        label: 'setting',
        key: '#setting#vulnerability-status-system',
      },
    });

    const setting = (response.data.settings ?? []).find(
      (s: { name: string }) => s.name === 'vulnerability-status-system'
    );

    const mode: VulnerabilityStatusMode = isValidMode(setting?.value)
      ? setting.value
      : 'legacy';

    valueMap = mode === 'new' ? NEW_VALUE_MAP : LEGACY_VALUE_MAP;
    valueMapInverse = invertMap(valueMap);
  } catch {
    // Default to legacy on error
    valueMap = LEGACY_VALUE_MAP;
    valueMapInverse = invertMap(valueMap);
  }

  return Object.values(valueMap);
}

/**
 * Reusable field definition for Risk status
 * Translates between API codes and mode-aware UI labels
 */
export const RiskStatus: SimpleFilterDefinition = {
  apiField: 'status',
  uiField: 'Status',
  inputType: 'dropdown',
  getAvailableValues: fetchStatusOptions,
  operators: ['is', 'is not'],

  translateForUI: (queryFilter: QueryFilter): SimpleFilter => {
    return {
      field: 'Status',
      operator: getOperatorFromQueryFilter(queryFilter, operatorMap),
      value: getValueFromQueryFilter(valueMap, queryFilter),
    };
  },

  matchesQueryFilter: (queryFilter: QueryFilter): boolean => {
    return (
      queryFilter.field === 'status' && queryFilter.operator === 'STARTS WITH'
    );
  },

  translateForAPI: (simpleFilter: SimpleFilter): QueryFilter => {
    return {
      field: 'status',
      operator: getOperatorFromSimpleFilter(simpleFilter, operatorMapInverse),
      value: getValueFromSimpleFilter(valueMapInverse, simpleFilter),
      not: getNotFromSimpleFilter(simpleFilter),
    };
  },
};
